/**
 * Optimized Variant Generator with Dynamic Updates
 * 
 * This class handles efficient variant generation and updates for product options,
 * implementing advanced algorithms for Cartesian product generation and dynamic
 * updates without full regeneration.
 * 
 * **Key Features:**
 * - O(m × n × p) base generation complexity
 * - Dynamic updates with targeted change detection
 * - Memory-efficient registry system with O(1) lookups
 * - Comprehensive error handling and validation
 * - Performance monitoring and health checks
 * 
 * **Usage Example:**
 * ```typescript
 * const generator = new VariantGenerator();
 * 
 * // Generate initial variants
 * const variants = generator.generateVariants([
 *   { id: "1", title: "Color", values: ["Red", "Blue"] },
 *   { id: "2", title: "Size", values: ["S", "M", "L"] }
 * ]);
 * 
 * // Dynamic updates
 * const updated = generator.addPropertyValue(0, "Green", options);
 * const metrics = generator.getMetrics();
 * ```
 * 
 * @author VariantSystem
 * @version 2.0.0
 * @since 1.0.0
 */

import { VariantOption } from "./VariantOptions";
import { VariantCombination } from "./VariantTable";
import { ShippingDetails } from "@/types/models/product";

/** @typedef {string} PropertyValue - A single value for a property (e.g., "Red", "Large") */
type PropertyValue = string;

/** @typedef {number} PropertyIndex - Zero-based index of a property in the options array */
type PropertyIndex = number;

/** @typedef {string} VariantKey - Serialized combination key for O(1) lookups (e.g., "Red::Large::Cotton") */
type VariantKey = string;

/**
 * Internal registry structure for efficient variant management
 * 
 * @interface VariantRegistry
 * @property {Map<VariantKey, VariantCombination>} variants - Primary variant storage with O(1) access
 * @property {Map<PropertyIndex, Set<PropertyValue>>} propertyMap - Track available values per property
 * @property {Map<VariantKey, PropertyIndex[]>} keyToIndices - Mapping for efficient updates
 */
interface VariantRegistry {
  variants: Map<VariantKey, VariantCombination>;
  propertyMap: Map<PropertyIndex, Set<PropertyValue>>;
  keyToIndices: Map<VariantKey, PropertyIndex[]>;
}

/**
 * Validation result interface for option validation
 * 
 * @interface ValidationResult
 * @property {boolean} isValid - Whether the validation passed
 * @property {string[]} errors - Array of validation error messages
 */
interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Performance metrics interface for monitoring
 * 
 * @interface PerformanceMetrics
 */
interface PerformanceMetrics {
  variantCount: number;
  propertyCount: number;
  memoryUsage: {
    variants: number;
    properties: number;
    keyMappings: number;
    estimatedKB: number;
  };
  performance: {
    maxVariantsLimit: number;
    maxPropertiesLimit: number;
    utilizationPercent: number;
    theoreticalComplexity: number;
    efficiency: number;
  };
  properties: Array<{
    index: number;
    valueCount: number;
    values: string[];
  }>;
  health: {
    isOptimal: boolean;
    hasOrphanedKeys: boolean;
    memoryPressure: 'LOW' | 'MEDIUM' | 'HIGH';
  };
  timestamp: string;
}

/**
 * Generates a stable, unique key for a variant combination
 */
function generateVariantKey(combination: PropertyValue[]): VariantKey {
  return combination.join("::"); // Using :: as separator to avoid conflicts
}

/**
 * Creates default shipping details
 */
function createDefaultShippingDetails(): ShippingDetails {
  return {
    weight: 0,
    weightUnit: "g",
    width: 0,
    height: 0,
    length: 0,
    shippingClass: "",
    freeShipping: false,
    shippingPrice: 0,
  };
}

/**
 * Creates a default variant when no option groups exist
 */
function createDefaultVariant(): VariantCombination {
  const defaultId = `default-${Date.now()}`;
  return {
    id: defaultId,
    options: ["Default"],
    optionString: "Default",
    image: undefined,
    price: 0,
    listingPrice: 0,
    sku: "",
    barcode: "",
    trackInventory: false,
    inventory: 0,
    title: "Default Variant",
    description: "",
    shippingDetails: createDefaultShippingDetails(),
    metafields: {},
  };
}

/**
 * Creates a new variant combination with proper defaults
 */
function createVariantCombination(
  combination: PropertyValue[],
  index: number,
  existingVariant?: VariantCombination,
): VariantCombination {
  const key = generateVariantKey(combination);

  return {
    id: existingVariant?.id || `variant-${key}`,
    options: [...combination], // Shallow copy to prevent mutations
    optionString: combination.join(" / "),
    image: existingVariant?.image,
    price: existingVariant?.price || 0,
    listingPrice: existingVariant?.listingPrice || 0,
    sku: existingVariant?.sku || `SKU-${index + 1}`,
    barcode: existingVariant?.barcode,
    trackInventory: existingVariant?.trackInventory ?? true,
    inventory: existingVariant?.inventory || 0,
    title: existingVariant?.title || "",
    description: existingVariant?.description || "",
    shippingDetails: existingVariant?.shippingDetails
      ? { ...existingVariant.shippingDetails } // Deep copy
      : createDefaultShippingDetails(),
    metafields: existingVariant?.metafields
      ? { ...existingVariant.metafields } // Shallow copy
      : {},
  };
}

/**
 * VariantGenerator - Advanced variant generation and management system
 * 
 * Handles efficient generation of product variants using Cartesian product algorithms
 * with sophisticated optimization for dynamic updates and memory management.
 * 
 * @class VariantGenerator
 * @example
 * ```typescript
 * const generator = new VariantGenerator();
 * 
 * // Basic usage
 * const options = [
 *   { id: "1", title: "Color", values: ["Red", "Blue"] },
 *   { id: "2", title: "Size", values: ["S", "M"] }
 * ];
 * 
 * const variants = generator.generateVariants(options);
 * console.log(`Generated ${variants.length} variants`); // 4 variants
 * 
 * // Performance monitoring
 * const metrics = generator.getMetrics();
 * const warnings = generator.getPerformanceWarnings();
 * ```
 */
export class VariantGenerator {
  /** @private Internal registry for variant storage and management */
  private registry: VariantRegistry;
  
  /** @private Maximum allowed variants to prevent performance issues */
  private readonly maxVariants: number = 1000;
  
  /** @private Maximum properties per variant (enforces e-commerce best practices) */
  private readonly maxPropertiesPerVariant: number = 3;

  /**
   * Initialize a new VariantGenerator instance
   * 
   * @constructor
   * @memberof VariantGenerator
   * @example
   * ```typescript
   * const generator = new VariantGenerator();
   * ```
   */
  constructor() {
    this.registry = {
      variants: new Map(),
      propertyMap: new Map(),
      keyToIndices: new Map(),
    };
  }

  /**
   * Validate variant options before processing
   * 
   * Performs comprehensive validation including:
   * - Property limits enforcement
   * - Required field validation  
   * - Duplicate value detection
   * - Complexity limit checking
   * 
   * @private
   * @param {VariantOption[]} options - Array of variant options to validate
   * @returns {ValidationResult} Validation result with success status and error details
   * @memberof VariantGenerator
   */
  private validateOptions(options: VariantOption[]): ValidationResult {
    const errors: string[] = [];

    // Check maximum properties limit
    if (options.length > this.maxPropertiesPerVariant) {
      errors.push(`Maximum ${this.maxPropertiesPerVariant} properties allowed`);
    }

    // Validate each option
    options.forEach((option, index) => {
      if (!option.title?.trim()) {
        errors.push(`Property ${index + 1} requires a title`);
      }

      if (!Array.isArray(option.values)) {
        errors.push(`Property "${option.title}" must have an array of values`);
      }

      if (option.values.length === 0) {
        errors.push(`Property "${option.title}" must have at least one value`);
      }

      // Check for duplicate values within the same property
      const uniqueValues = new Set(option.values);
      if (uniqueValues.size !== option.values.length) {
        errors.push(`Property "${option.title}" has duplicate values`);
      }
    });

    // Check if total combinations would exceed maximum
    const totalCombinations = options.reduce(
      (total, option) => total * (option.values?.length || 1),
      1,
    );

    if (totalCombinations > this.maxVariants) {
      errors.push(
        `Total variant combinations (${totalCombinations}) would exceed maximum (${this.maxVariants})`
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate property index for dynamic operations
   */
  private validatePropertyIndex(index: number, options: VariantOption[]): boolean {
    return index >= 0 && index < options.length;
  }

  /**
   * Safe wrapper for operations that might fail
   */
  private safeOperation<T>(
    operation: () => T,
    fallback: T,
    errorContext: string,
  ): T {
    try {
      return operation();
    } catch (error) {
      console.error(`VariantGenerator Error [${errorContext}]:`, error);
      return fallback;
    }
  }

  /**
   * Generate all possible variant combinations from property options
   * 
   * This is the primary method for creating variants from property definitions.
   * Uses efficient Cartesian product algorithm with validation and error handling.
   * 
   * **Algorithm Details:**
   * - Time Complexity: O(m × n × p) where m, n, p are property value array sizes
   * - Space Complexity: O(m × n × p) for storing combinations
   * - Memory-efficient iterative approach (not recursive)
   * - Preserves existing variant data when regenerating
   * 
   * @public
   * @param {VariantOption[]} options - Array of property definitions with values
   * @returns {VariantCombination[]} Array of generated variant combinations
   * @memberof VariantGenerator
   * 
   * @example
   * ```typescript
   * const options = [
   *   { id: "1", title: "Color", values: ["Red", "Blue", "Green"] },
   *   { id: "2", title: "Size", values: ["S", "M", "L"] },
   *   { id: "3", title: "Material", values: ["Cotton", "Polyester"] }
   * ];
   * 
   * const variants = generator.generateVariants(options);
   * console.log(variants.length); // 18 variants (3 × 3 × 2)
   * 
   * // Each variant contains:
   * console.log(variants[0]);
   * // {
   * //   id: "variant-Red::S::Cotton",
   * //   options: ["Red", "S", "Cotton"],
   * //   optionString: "Red / S / Cotton", 
   * //   price: 0,
   * //   sku: "SKU-1",
   * //   // ... other fields
   * // }
   * ```
   * 
   * @throws {Error} Implicitly handles errors via safeOperation wrapper
   * @see {@link addPropertyValue} for dynamic value addition
   * @see {@link validateOptions} for validation details
   */
  generateVariants(options: VariantOption[]): VariantCombination[] {
    return this.safeOperation(
      () => {
        console.log('🔧 VariantGenerator.generateVariants called with:', options);
        
        // Filter and validate options
        const validOptions = options.filter(
          (opt) => opt.values && opt.values.length > 0,
        );

        console.log('✅ Valid options after filtering:', validOptions);

        if (validOptions.length === 0) {
          // No option groups - create default variant
          console.log('🚫 No valid options, creating default variant');
          this.registry.variants.clear();
          this.registry.propertyMap.clear();
          this.registry.keyToIndices.clear();

          const defaultVariant = createDefaultVariant();
          this.registry.variants.set("default", defaultVariant);
          return [defaultVariant];
        }

        // Validate options before processing
        const validation = this.validateOptions(validOptions);
        console.log('🔍 Validation result:', validation);
        
        if (!validation.isValid) {
          console.warn("Variant generation validation failed:", validation.errors);
          // Return existing variants if validation fails
          return Array.from(this.registry.variants.values());
        }

        // Update property map for tracking
        this.updatePropertyMap(validOptions);

        // Generate combinations using iterative approach (more memory efficient)
        const combinations = this.generateCombinationsIterative(
          validOptions.map((opt) => opt.values),
        );

        console.log('🎯 Generated combinations:', combinations.length, combinations);

        // Create or update variants efficiently
        const newVariants = new Map<VariantKey, VariantCombination>();
        const existingVariants = this.registry.variants;

        combinations.forEach((combination, index) => {
          const key = generateVariantKey(combination);
          const existingVariant = existingVariants.get(key);

          const variant = createVariantCombination(
            combination,
            index,
            existingVariant,
          );
          newVariants.set(key, variant);

          // Update key to indices mapping
          this.registry.keyToIndices.set(
            key,
            combination.map((_, idx) => idx),
          );
        });

        console.log('🏗️ Created variants map with size:', newVariants.size);

        // Replace registry with new variants (atomic update)
        this.registry.variants = newVariants;

        const finalVariants = Array.from(newVariants.values());
        console.log('🎉 Final variants being returned:', finalVariants.length, finalVariants);

        return finalVariants;
      },
      // Fallback: return current variants if generation fails
      Array.from(this.registry.variants.values()),
      "generateVariants"
    );
  }

  /**
   * Iterative combination generation to avoid nested operations and reduce memory overhead
   */
  private generateCombinationsIterative(
    propertyArrays: PropertyValue[][],
  ): PropertyValue[][] {
    console.log('⚙️ generateCombinationsIterative called with:', propertyArrays);
    
    if (propertyArrays.length === 0) return [];
    if (propertyArrays.length === 1) {
      const result = propertyArrays[0].map((val) => [val]);
      console.log('📝 Single property result:', result);
      return result;
    }

    // Start with first property as base
    let combinations: PropertyValue[][] = propertyArrays[0].map((val) => [val]);
    console.log('🔤 Starting combinations from first property:', combinations);

    // Iteratively combine with remaining properties
    for (let i = 1; i < propertyArrays.length; i++) {
      const currentProperty = propertyArrays[i];
      const newCombinations: PropertyValue[][] = [];
      console.log(`🔄 Processing property ${i}:`, currentProperty);

      // Combine each existing combination with current property values
      for (const existingCombination of combinations) {
        for (const value of currentProperty) {
          // Create new combination without mutating existing
          const newCombination = [...existingCombination, value];
          newCombinations.push(newCombination);
        }
      }

      combinations = newCombinations;
      console.log(`✨ After processing property ${i}, combinations:`, combinations.length);
    }

    console.log('🎊 Final combinations generated:', combinations);
    return combinations;
  }

  /**
   * Update property map for efficient tracking of property changes
   */
  private updatePropertyMap(options: VariantOption[]): void {
    this.registry.propertyMap.clear();
    options.forEach((option, index) => {
      this.registry.propertyMap.set(index, new Set(option.values));
    });
  }

  /**
   * Dynamic Update: Add new property value efficiently
   * Only generates new combinations involving the new value
   */
  addPropertyValue(
    propertyIndex: PropertyIndex,
    newValue: PropertyValue,
    options: VariantOption[],
  ): VariantCombination[] {
    console.log(`⚡ VariantGenerator.addPropertyValue called with:`, {
      propertyIndex,
      newValue,
      options: options.map(o => ({ title: o.title, values: o.values })),
      hasRegistry: !!this.registry,
      registryVariantCount: this.registry?.variants.size || 0,
      registryPropertyMapSize: this.registry?.propertyMap.size || 0
    });

    return this.safeOperation(
      () => {
        console.log(`🔒 Inside safeOperation for addPropertyValue`);
        
        // Validate inputs
        if (!this.validatePropertyIndex(propertyIndex, options)) {
          console.warn(`❌ Invalid property index: ${propertyIndex}`);
          return Array.from(this.registry.variants.values());
        }

        if (!newValue?.trim()) {
          console.warn("❌ Cannot add empty property value");
          return Array.from(this.registry.variants.values());
        }

        const propertySet = this.registry.propertyMap.get(propertyIndex);
        console.log(`🔍 Property set for index ${propertyIndex}:`, {
          propertySet: propertySet ? Array.from(propertySet) : null,
          hasNewValue: propertySet?.has(newValue)
        });

        if (!propertySet || propertySet.has(newValue)) {
          console.log(`❌ Value already exists or invalid property index - returning existing variants`);
          // Value already exists or invalid property index
          return Array.from(this.registry.variants.values());
        }

        // Check if adding this value would exceed maximum variants
        const currentVariantCount = this.registry.variants.size;
        const otherPropertiesSizes = Array.from(this.registry.propertyMap.values())
          .filter((_, idx) => idx !== propertyIndex)
          .map(set => set.size);
        
        const additionalVariants = otherPropertiesSizes.reduce((total, size) => total * size, 1);
        
        if (currentVariantCount + additionalVariants > this.maxVariants) {
          console.warn(`Adding value "${newValue}" would exceed maximum variants limit`);
          return Array.from(this.registry.variants.values());
        }

        // Add new value to property set
        propertySet.add(newValue);

        // Generate only new combinations involving the new value
        const newCombinations = this.generateNewCombinationsForValue(
          propertyIndex,
          newValue,
          options,
        );

        // Add new variants to registry
        newCombinations.forEach((combination, index) => {
          const key = generateVariantKey(combination);
          if (!this.registry.variants.has(key)) {
            const variant = createVariantCombination(
              combination,
              currentVariantCount + index,
            );
            this.registry.variants.set(key, variant);
            this.registry.keyToIndices.set(
              key,
              combination.map((_, idx) => idx),
            );
          }
        });

        return Array.from(this.registry.variants.values());
      },
      Array.from(this.registry.variants.values()),
      "addPropertyValue"
    );
  }

  /**
   * Generate new combinations for a specific property value addition
   */
  private generateNewCombinationsForValue(
    targetPropertyIndex: PropertyIndex,
    newValue: PropertyValue,
    options: VariantOption[],
  ): PropertyValue[][] {
    const validOptions = options.filter(
      (opt) => opt.values && opt.values.length > 0,
    );
    const propertyArrays = validOptions.map((opt) => opt.values);

    if (targetPropertyIndex >= propertyArrays.length) return [];

    // Generate combinations with the new value at the target index
    const otherProperties = propertyArrays.map((arr, idx) =>
      idx === targetPropertyIndex ? [newValue] : arr,
    );

    return this.generateCombinationsIterative(otherProperties);
  }

  /**
   * Dynamic Update: Remove property value and clean up affected variants
   */
  removePropertyValue(
    propertyIndex: PropertyIndex,
    valueToRemove: PropertyValue,
  ): VariantCombination[] {
    console.log(`🗑️ VariantGenerator.removePropertyValue called with:`, {
      propertyIndex,
      valueToRemove,
      hasRegistry: !!this.registry,
      registryVariantCount: this.registry?.variants.size || 0
    });

    const propertySet = this.registry.propertyMap.get(propertyIndex);
    console.log(`🔍 Property set for index ${propertyIndex}:`, {
      propertySet: propertySet ? Array.from(propertySet) : null,
      hasValueToRemove: propertySet?.has(valueToRemove)
    });

    if (!propertySet || !propertySet.has(valueToRemove)) {
      console.log(`❌ Property set not found or value not in set, returning existing variants`);
      return Array.from(this.registry.variants.values());
    }

    // Remove value from property set
    propertySet.delete(valueToRemove);
    console.log(`✅ Removed "${valueToRemove}" from property set`);

    // Remove affected variants
    const keysToRemove: VariantKey[] = [];

    this.registry.variants.forEach((variant, key) => {
      if (variant.options[propertyIndex] === valueToRemove) {
        keysToRemove.push(key);
      }
    });

    console.log(`🎯 Found ${keysToRemove.length} variants to remove`);

    // Batch removal for efficiency
    keysToRemove.forEach((key) => {
      this.registry.variants.delete(key);
      this.registry.keyToIndices.delete(key);
    });

    const remainingVariants = Array.from(this.registry.variants.values());
    console.log(`🗑️ removePropertyValue completed, ${remainingVariants.length} variants remaining`);

    return remainingVariants;
  }

  /**
   * Dynamic Update: Update existing property value across all variants
   */
  updatePropertyValue(
    propertyIndex: PropertyIndex,
    oldValue: PropertyValue,
    newValue: PropertyValue,
  ): VariantCombination[] {
    const propertySet = this.registry.propertyMap.get(propertyIndex);

    if (
      !propertySet ||
      !propertySet.has(oldValue) ||
      propertySet.has(newValue)
    ) {
      return Array.from(this.registry.variants.values());
    }

    // Update property set
    propertySet.delete(oldValue);
    propertySet.add(newValue);

    // Update affected variants efficiently
    const updatedVariants = new Map<VariantKey, VariantCombination>();

    this.registry.variants.forEach((variant, oldKey) => {
      if (variant.options[propertyIndex] === oldValue) {
        // Create updated combination
        const updatedOptions = [...variant.options];
        updatedOptions[propertyIndex] = newValue;

        const newKey = generateVariantKey(updatedOptions);
        const updatedVariant = {
          ...variant,
          options: updatedOptions,
          optionString: updatedOptions.join(" / "),
        };

        updatedVariants.set(newKey, updatedVariant);

        // Update key mapping
        this.registry.keyToIndices.delete(oldKey);
        this.registry.keyToIndices.set(
          newKey,
          updatedOptions.map((_, idx) => idx),
        );
      } else {
        updatedVariants.set(oldKey, variant);
      }
    });

    this.registry.variants = updatedVariants;
    return Array.from(updatedVariants.values());
  }

  /**
   * Property-level changes: Remove entire property
   */
  removeProperty(
    propertyIndex: PropertyIndex,
    options: VariantOption[],
  ): VariantCombination[] {
    // Remove property from tracking
    this.registry.propertyMap.delete(propertyIndex);

    // Regenerate all variants with remaining properties
    const remainingOptions = options.filter(
      (_, index) => index !== propertyIndex,
    );
    return this.generateVariants(remainingOptions);
  }

  /**
   * Property-level changes: Add new property
   */
  addProperty(
    newPropertyValues: PropertyValue[],
    options: VariantOption[],
  ): VariantCombination[] {
    // Regenerate all variants including the new property
    return this.generateVariants(options);
  }

  /**
   * Get current variants without regeneration
   */
  getCurrentVariants(): VariantCombination[] {
    return Array.from(this.registry.variants.values());
  }

  /**
   * Update a specific variant's data (non-structural changes)
   */
  updateVariant(
    variantKey: VariantKey,
    updates: Partial<VariantCombination>,
  ): VariantCombination[] {
    const existingVariant = this.registry.variants.get(variantKey);

    if (existingVariant) {
      const updatedVariant = { ...existingVariant, ...updates };
      this.registry.variants.set(variantKey, updatedVariant);
    }

    return Array.from(this.registry.variants.values());
  }

  /**
   * Delete a specific variant from the registry
   * Provides proper cleanup of all registry mappings
   */
  deleteVariant(variantKey: VariantKey): VariantCombination[] {
    // Remove from variants registry
    this.registry.variants.delete(variantKey);
    
    // Remove from key mapping registry
    this.registry.keyToIndices.delete(variantKey);

    // Note: We don't update propertyMap since deleting individual variants
    // doesn't affect the available property values

    return Array.from(this.registry.variants.values());
  }

  /**
   * Get variant by combination for efficient lookups
   */
  getVariantByOptions(
    options: PropertyValue[],
  ): VariantCombination | undefined {
    const key = generateVariantKey(options);
    return this.registry.variants.get(key);
  }

  /**
   * Clear all variants and reset state
   */
  clear(): void {
    this.registry.variants.clear();
    this.registry.propertyMap.clear();
    this.registry.keyToIndices.clear();
  }

  /**
   * Get comprehensive performance metrics and health indicators
   * 
   * Provides detailed information about the generator's current state,
   * performance characteristics, and health indicators for monitoring
   * and optimization purposes.
   * 
   * @public
   * @returns {PerformanceMetrics} Detailed metrics object with performance data
   * @memberof VariantGenerator
   * 
   * @example
   * ```typescript
   * const metrics = generator.getMetrics();
   * 
   * console.log(`Variants: ${metrics.variantCount}/${metrics.performance.maxVariantsLimit}`);
   * console.log(`Memory: ${metrics.memoryUsage.estimatedKB}KB`);
   * console.log(`Efficiency: ${metrics.performance.efficiency}%`);
   * console.log(`Health: ${metrics.health.isOptimal ? 'Good' : 'Needs attention'}`);
   * 
   * // Monitor memory pressure
   * if (metrics.health.memoryPressure === 'HIGH') {
   *   console.warn('High memory usage detected');
   * }
   * 
   * // Property breakdown
   * metrics.properties.forEach((prop, index) => {
   *   console.log(`Property ${index}: ${prop.valueCount} values`);
   * });
   * ```
   * 
   * @see {@link getPerformanceWarnings} for specific recommendations
   * @since 2.0.0
   */
  getMetrics(): PerformanceMetrics {
    const variants = Array.from(this.registry.variants.values());
    const propertyValues = Array.from(this.registry.propertyMap.values());
    
    // Calculate memory estimations
    const avgVariantSize = variants.length > 0 
      ? JSON.stringify(variants[0]).length 
      : 0;
    
    const estimatedMemoryKB = Math.round(
      (variants.length * avgVariantSize + 
       this.registry.keyToIndices.size * 50) / 1024
    );

    // Performance indicators
    const complexity = propertyValues.reduce((total, set) => total * set.size, 1);
    const efficiency = variants.length > 0 ? variants.length / complexity : 1;

    return {
      // Basic counts
      variantCount: this.registry.variants.size,
      propertyCount: this.registry.propertyMap.size,
      
      // Memory usage details
      memoryUsage: {
        variants: this.registry.variants.size,
        properties: propertyValues.reduce((sum, set) => sum + set.size, 0),
        keyMappings: this.registry.keyToIndices.size,
        estimatedKB: estimatedMemoryKB,
      },
      
      // Performance metrics
      performance: {
        maxVariantsLimit: this.maxVariants,
        maxPropertiesLimit: this.maxPropertiesPerVariant,
        utilizationPercent: Math.round((variants.length / this.maxVariants) * 100),
        theoreticalComplexity: complexity,
        efficiency: Math.round(efficiency * 100),
      },
      
      // Property breakdown
      properties: propertyValues.map((set, index) => ({
        index,
        valueCount: set.size,
        values: Array.from(set),
      })),
      
      // Health indicators
      health: {
        isOptimal: variants.length <= this.maxVariants * 0.8,
        hasOrphanedKeys: this.registry.keyToIndices.size !== variants.length,
        memoryPressure: estimatedMemoryKB > 1024 ? 'HIGH' : 
                       estimatedMemoryKB > 512 ? 'MEDIUM' : 'LOW',
      },
      
      // Timestamp for monitoring
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Get performance warnings and recommendations
   */
  getPerformanceWarnings(): string[] {
    const metrics = this.getMetrics();
    const warnings: string[] = [];

    if (metrics.variantCount > this.maxVariants * 0.8) {
      warnings.push(`Approaching maximum variant limit (${metrics.variantCount}/${this.maxVariants})`);
    }

    if (metrics.memoryUsage.estimatedKB > 1024) {
      warnings.push(`High memory usage detected: ${metrics.memoryUsage.estimatedKB}KB`);
    }

    if (metrics.health.hasOrphanedKeys) {
      warnings.push('Registry inconsistency detected - consider regenerating variants');
    }

    if (metrics.performance.efficiency < 50) {
      warnings.push('Low efficiency detected - many theoretical combinations unused');
    }

    return warnings;
  }
}

export default VariantGenerator;
